import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown, LogOut, User as UserIcon, Building2, Menu, FlaskConical } from "lucide-react";
import { useAuth, ROLE_LABELS, ROLE_ORDER } from "@/lib/auth";
import { api, isTestDataShown, setTestDataShown } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";

export default function Topbar({ fundId, setFundId, onMenuClick }) {
  const { user, logout, switchRole } = useAuth();
  const navigate = useNavigate();
  const [funds, setFunds] = useState([]);

  useEffect(() => {
    if (user?.active_role === "investor" || user?.active_role === "distributor") return;
    api.get("/funds").then((r) => setFunds(r.data)).catch(() => {});
  }, [user?.active_role]);

  const currentFund = funds.find((f) => f.id === fundId);
  // Sort roles by authority hierarchy — AIFLedger Owner → LP.
  const roles = [...(user?.roles || [])].sort(
    (a, b) => (ROLE_ORDER.indexOf(a) === -1 ? 999 : ROLE_ORDER.indexOf(a))
            - (ROLE_ORDER.indexOf(b) === -1 ? 999 : ROLE_ORDER.indexOf(b))
  );
  // Owner-set per-practice prefix — e.g. "Motilal" renders roles as
  // "Motilal Admin" / "Motilal Sub-Admin". Only applied to the two roles that
  // Admins actually get confused about; Owner stays "Owner" and specialist
  // roles keep their own name (Accountant, Compliance Officer, …).
  const labelPrefix = (user?.practice_branding?.admin_label_prefix || "").trim();
  const PREFIXABLE_ROLES = new Set(["super_admin", "fund_admin"]);
  const roleLabel = (r) => {
    const base = ROLE_LABELS[r] || r;
    if (labelPrefix && PREFIXABLE_ROLES.has(r)) return `${labelPrefix} ${base}`;
    return base;
  };
  const isInvestor = user?.active_role === "investor";
  const isDistributor = user?.active_role === "distributor";
  const hideScopedControls = isInvestor || isDistributor;

  // Test-data toggle — persisted in localStorage, watched here so switching
  // the toggle instantly re-renders the label (and every subsequent list
  // request goes out with `include_test=true` via the axios interceptor).
  const [showTest, setShowTest] = useState(isTestDataShown());
  useEffect(() => {
    const h = () => setShowTest(isTestDataShown());
    window.addEventListener("aif-test-data-toggled", h);
    return () => window.removeEventListener("aif-test-data-toggled", h);
  }, []);
  const toggleTest = () => {
    const next = !showTest;
    setTestDataShown(next);
    setShowTest(next);
    // Give every open list a chance to re-fetch — cheapest way is a hard reload
    // right now (pages don't listen for the event). Small delay keeps the
    // toast visible before the reload wipes it.
    setTimeout(() => window.location.reload(), 250);
  };

  return (
    <header className="h-16 border-b border-[#e2e1dd] bg-[#fdfcf8]/90 backdrop-blur-md flex items-center px-3 sm:px-6 gap-2 sm:gap-4 sticky top-0 z-30" data-testid="topbar">
      {/* Hamburger — mobile only */}
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 -ml-1 rounded-md hover:bg-[#f5f4f0] text-[#0b192c]"
        data-testid="topbar-menu-toggle"
        aria-label="Open menu"
      >
        <Menu size={18} />
      </button>

      {/* Fund/scheme selector — hidden on xs, hidden entirely for investor + distributor */}
      {!hideScopedControls && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#e2e1dd] hover:bg-[#f5f4f0] transition text-sm" data-testid="fund-selector">
              <Building2 size={14} className="text-[#475569]" />
              <span className="text-[#0f172a] font-medium truncate max-w-[140px] lg:max-w-[200px]">
                {currentFund ? currentFund.name : "All Funds"}
              </span>
              <ChevronDown size={14} className="text-[#475569]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72">
            <DropdownMenuLabel>Select fund</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => setFundId(null)} data-testid="fund-option-all">
              <span className="font-medium">All Funds</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {funds.map((f) => (
              <DropdownMenuItem key={f.id} onClick={() => setFundId(f.id)} data-testid={`fund-option-${f.id}`}>
                <div>
                  <div className="font-medium text-[#0f172a]">{f.name}</div>
                  <div className="text-xs text-[#475569]">{f.category} · {f.sub_category}</div>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Global search — hidden on xs, hidden entirely for investor + distributor */}
      {!hideScopedControls && (
        <div className="hidden md:flex flex-1 max-w-md relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <input
            className="w-full pl-9 pr-4 py-1.5 text-sm bg-white border border-[#e2e1dd] rounded-md focus:outline-none focus:ring-2 focus:ring-[#c4a454] focus:border-transparent"
            placeholder="Search investors, funds, documents…"
            data-testid="global-search"
          />
        </div>
      )}

      <div className="flex-1" />

      {/* Test-data toggle — hidden for LPs & distributors, hidden until the
          user has been onboarded past demo. Renders as a subtle pill so it
          doesn't distract from real work, but glows amber when ON so it's
          obvious the current view contains QA/demo rows. */}
      {!hideScopedControls && !user?.is_demo && (
        <button onClick={toggleTest}
                title={showTest ? "Hiding test data restores the default clean view." : "Show TEST / DEMO records normally hidden from every module."}
                className={`hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition ${
                  showTest
                    ? "bg-[#faf1d6] text-[#7a5a12] border border-[#e0c983]"
                    : "bg-white text-[#475569] border border-[#e2e1dd] hover:bg-[#f5f4f0]"
                }`}
                data-testid="test-data-toggle">
          <FlaskConical size={12} />
          <span className="hidden lg:inline">{showTest ? "Showing test data" : "Hide test data"}</span>
        </button>
      )}

      {/* Role switcher */}
      {roles.length > 1 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-md bg-[#0b192c] text-white text-xs font-semibold hover:bg-[#0b192c]/90" data-testid="role-switcher">
              <span style={{ color: "#c4a454" }}>●</span>
              <span className="hidden sm:inline">{roleLabel(user.active_role)}</span>
              <ChevronDown size={12} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>Switch active role</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {roles.map((r) => (
              <DropdownMenuItem
                key={r}
                onClick={() => switchRole(r).then(() => navigate(r === "investor" ? "/investor" : "/dashboard"))}
                data-testid={`role-option-${r}`}
              >
                {roleLabel(r)}
                {user.active_role === r ? <span className="ml-auto text-[#c4a454]">●</span> : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Notifications */}
      <NotificationBell />

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 pl-2 pr-3 py-1 rounded-md hover:bg-[#f5f4f0]" data-testid="user-menu">
            <div className="w-7 h-7 rounded-full bg-[#0b192c] text-white flex items-center justify-center text-xs font-semibold">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="text-left hidden lg:block">
              <div className="text-xs font-semibold text-[#0f172a] leading-none">{user?.name}</div>
              <div className="text-[10px] text-[#475569] mt-0.5">{user?.email}</div>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="user-menu-profile">
            <UserIcon size={14} className="mr-2" /> Profile
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="user-menu-logout">
            <LogOut size={14} className="mr-2" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
